import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Slider from "@mui/material/Slider";
import { genresData } from "../../../constants/structure";
import { RoomFilters } from "../../../api/room";
import ClearButton from "../../filters/components/ClearButton";
import styles from "./RoomEntry.module.scss";

interface RoomEntryProps {
  onCreateRoom: (filters: RoomFilters) => void;
  onJoinRoom: (code: string) => void;
  loading: boolean;
  error: string;
}

type Tab = "create" | "join";

const MIN_YEAR = 1980;
const MAX_YEAR = new Date().getFullYear();
const MIN_DISTANCE = 0;

const RoomEntry: React.FC<RoomEntryProps> = ({
                                               onCreateRoom,
                                               onJoinRoom,
                                               loading,
                                               error,
                                             }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>("create");
  const [joinCode, setJoinCode] = useState("");
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [yearRange, setYearRange] = useState<number[]>([MIN_YEAR, MAX_YEAR]);

  const handleCreate = () => {
    const filters: RoomFilters = {};
    if (selectedGenres.length) filters.genre_ids = selectedGenres;
    if (yearRange[0] !== MIN_YEAR) filters.year_from = yearRange[0];
    if (yearRange[1] !== MAX_YEAR) filters.year_to = yearRange[1];
    onCreateRoom(filters);
  };

  const handleJoin = () => {
    if (!joinCode.trim()) return;
    onJoinRoom(joinCode.trim().toUpperCase());
  };

  const toggleGenre = (id: number) => {
    setSelectedGenres((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  const handleYearChange = (
    _event: Event,
    newValue: number | number[],
    activeThumb: number
  ) => {
    if (!Array.isArray(newValue)) return;

    if (activeThumb === 0) {
      setYearRange([Math.min(newValue[0], yearRange[1] - MIN_DISTANCE), yearRange[1]]);
    } else {
      setYearRange([yearRange[0], Math.max(newValue[1], yearRange[0] + MIN_DISTANCE)]);
    }
  };

  const clearYearFilter = () => {
    setYearRange([MIN_YEAR, MAX_YEAR]);
  };

  const clearGenresFilter = () => {
    setSelectedGenres([]);
  };

  return (
    <div className={styles.entry}>
      <div className={styles.entry_heading}>
        <div className={styles.title}>{t("room_public_room", "Public Room")}</div>
        <div className={styles.subtitle}>
          {t("room_subtitle", "Create a room and watch movies together with friends")}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.roomTabs}>
        <button
          className={`${styles.roomTab} ${activeTab === "create" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("create")}
        >
          Создать
        </button>
        <button
          className={`${styles.roomTab} ${activeTab === "join" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("join")}
        >
          Присоединиться
        </button>
      </div>

      {activeTab === "create" && (
        <div className={styles.section}>
          <div className={styles.periodWrapper}>
            <div className={styles.periodTitle}>
              <div className={styles.periodTitleHolder}>
                <span>
                Период
              </span>
                <ClearButton
                  clearFiltersHandler={clearYearFilter}
                  hideBtn={yearRange[0] === MIN_YEAR && yearRange[1] === MAX_YEAR}
                />
              </div>
            </div>
            <div className={styles.dateWrapper}>
              <div className={styles.dateItem}>{yearRange[0]}</div>
              <div className={styles.dateItem}>{yearRange[1]}</div>
            </div>
            <Slider
              min={MIN_YEAR}
              max={MAX_YEAR}
              value={yearRange}
              onChange={handleYearChange}
              disableSwap
              getAriaLabel={() => "Year range"}
              className="period-controller"
            />
          </div>

          <div className="genreGridSection">
            <div className={styles.genreGridTitle}>
              <div className={styles.periodTitleHolder}>
                <span>
                  Жанр
                  <ClearButton
                    clearFiltersHandler={clearGenresFilter}
                    hideBtn={selectedGenres.length === 0}
                  />
                </span>
              </div>
            </div>
            <div className={styles.genreGrid}>
              {genresData.map((genre) => (
                <button
                  key={genre.id}
                  className={`${styles.genreChip} ${
                    selectedGenres.includes(genre.id) ? styles.genreChipActive : ""
                  }`}
                  onClick={() => toggleGenre(genre.id)}
                >
                  {t(genre.name)}
                </button>
              ))}
            </div>
          </div>

          <button
            className={styles.btnPrimary}
            onClick={handleCreate}
            disabled={loading}
          >
            {loading
              ? t("room_creating", "Creating...")
              : t("room_create_btn", "Create Room")}
          </button>
        </div>
      )}

      {activeTab === "join" && (
        <div className={styles.section}>
          <div className={styles.joinRow}>
            <div className={styles.inputWrap}>
              <label htmlFor="joinCode">{t("room_enter_code", "Enter room code")}</label>
              <input
                id="joinCode"
                maxLength={6}
                placeholder="******"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && handleJoin()}
              />
            </div>

            <button
              className={styles.btnPrimary}
              onClick={handleJoin}
              disabled={loading || !joinCode.trim()}
            >
              {t("room_join_btn", "Join")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomEntry;
